import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  getEventList,
  getImgEvent,
  getRequestList,
} from "../../../axios/Eventlog/EventLogApi";
import { useAuth } from "../../../context/AuthContext";
import { useLoading } from "../../../context/LoadingContext";

ChartJS.register(ArcElement, Tooltip, Legend);

const createCenterTextPlugin = (pluginId) => ({
  id: pluginId,
  beforeDraw(chart) {
    const { width, height, ctx } = chart;
    const pluginOptions = chart?.config?.options?.plugins?.[pluginId] ?? {};
    const total = pluginOptions.total ?? 0;
    const title = pluginOptions.title ?? "";

    ctx.restore();

    const titleFont = Math.round(width / 18);
    const valueFont = Math.round(width / 8);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const maxWidth = width * 0.35;
    const words = title.split(" ");
    const lines = [];
    let line = "";

    ctx.font = `${titleFont}px sans-serif`;

    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;
      if (ctx.measureText(testLine).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });

    if (line) lines.push(line);

    const lineHeight = titleFont + 2;
    const startY = height / 2 - (lines.length * lineHeight) / 2 - valueFont / 2;

    ctx.fillStyle = "#1F2937";
    lines.forEach((text, index) => {
      ctx.fillText(text, width / 2, startY + index * lineHeight);
    });

    ctx.font = `600 ${valueFont}px sans-serif`;
    ctx.fillText(String(total), width / 2, startY + lines.length * lineHeight + valueFont);
    ctx.save();
  },
});

const buildFromToByDateWithTZ = (selectedDate, timeZone) => {
  const base = DateTime.fromISO(
    typeof selectedDate === "string"
      ? selectedDate
      : DateTime.fromJSDate(selectedDate).toISODate(),
    { zone: timeZone }
  );

  return {
    from: base.startOf("day").toUTC().toISO(),
    to: base.endOf("day").toUTC().toISO(),
  };
};

const EventLogChart = ({
  chartType,
  selectedDate,
  selectedUser,
  selectedShift,
  selectGroup,
  selectDevice,
  selectStartTime,
  selectEndTime,
  refreshData,
}) => {
  const { t } = useTranslation();
  const [show, hide] = useLoading();
  const { genaralSettingTimeZone, isAdmin, isEmployee, profile } = useAuth();
  const [chartCounts, setChartCounts] = useState({
    segment1: 0,
    segment2: 0,
    segment3: 0,
    total: 0,
  });
  const chartContainerRef = useRef(null);
  const [chartContainerWidth, setChartContainerWidth] = useState(0);

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return;

    const updateSize = () => {
      setChartContainerWidth(Math.round(node.getBoundingClientRect().width));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    window.addEventListener("resize", updateSize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  const centerPluginId = `eventLogChartCenter_${chartType}`;
  const centerTextPlugin = useMemo(
    () => createCenterTextPlugin(centerPluginId),
    [centerPluginId]
  );

  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedDate) return;

      try {
        show();
        const timeZone = genaralSettingTimeZone || "UTC";
        const { from, to } = buildFromToByDateWithTZ(selectedDate, timeZone);
        const langId = localStorage.getItem("i18nextLngId");
        const userId = (isAdmin ? selectedUser?.id : profile?.userId) ?? "";

        if (chartType === "events") {
          const { data } = await getEventList(
            1,
            10000,
            from,
            to,
            userId,
            selectedShift === "all" ? "" : selectedShift,
            false,
            true,
            langId,
            selectGroup
          );

          const eventsList = Array.isArray(data?.data) ? data.data : [];
          const checkIn = eventsList.filter(
            (e) => e.eventType === "CHECK_IN"
          ).length;
          const checkOut = eventsList.filter(
            (e) => e.eventType === "CHECK_OUT"
          ).length;
          const pending = eventsList.filter((e) =>
            ["PENDING_CHECK_IN", "PENDING_CHECK_OUT"].includes(e.eventType)
          ).length;
          const uniqueUsers = new Set(
            eventsList.map((e) => e.userId).filter(Boolean)
          ).size;

          setChartCounts({
            segment1: checkIn,
            segment2: checkOut,
            segment3: pending,
            total: uniqueUsers || eventsList.length,
          });
          return;
        }

        if (chartType === "requests") {
          const { data } = await getRequestList({
            page: 1,
            record: 10000,
            from,
            to,
            userId,
            langId,
            requestType: "EVENT",
          });

          const requestsList = Array.isArray(data?.data) ? data.data : [];
          const approved = requestsList.filter(
            (r) => r.requestStatusName === "APPROVED"
          ).length;
          const rejected = requestsList.filter(
            (r) => r.requestStatusName === "REJECTED"
          ).length;
          const pending = requestsList.filter(
            (r) => r.requestStatusName === "PENDING"
          ).length;

          setChartCounts({
            segment1: approved,
            segment2: rejected,
            segment3: pending,
            total: requestsList.length,
          });
          return;
        }

        if (chartType === "images") {
          const { data } = await getImgEvent({
            page: 1,
            record: 10000,
            from,
            to,
            isError: false,
            deviceId: selectDevice,
            startTime: selectStartTime,
            endTime: selectEndTime,
            langId,
          });

          const imagesList = Array.isArray(data?.data) ? data.data : [];
          const isCheckIn = (i) => i.eventTypeId === 1;
          const isCheckOut = (i) => i.eventTypeId === 2;
          const checkIn = imagesList.filter(isCheckIn).length;
          const checkOut = imagesList.filter(isCheckOut).length;
          // Không có eventTypeId (kể cả đã nhận diện fullName) → hình ảnh không có sự kiện
          const withoutEvent = imagesList.filter(
            (i) => !isCheckIn(i) && !isCheckOut(i)
          ).length;
          const uniqueUsers = new Set(
            imagesList.map((i) => i.userId).filter(Boolean)
          ).size;

          setChartCounts({
            segment1: checkIn,
            segment2: checkOut,
            segment3: withoutEvent,
            total: uniqueUsers || imagesList.length,
          });
        }
      } catch (error) {
        console.error(error);
        setChartCounts({
          segment1: 0,
          segment2: 0,
          segment3: 0,
          total: 0,
        });
      } finally {
        hide();
      }
    };

    fetchChartData();
  }, [
    chartType,
    selectedDate,
    selectedUser?.id,
    selectedShift,
    selectGroup,
    selectDevice,
    selectStartTime,
    selectEndTime,
    refreshData,
    genaralSettingTimeZone,
    isAdmin,
    isEmployee,
    profile?.userId,
    show,
    hide,
  ]);

  const chartConfig = useMemo(() => {
    if (chartType === "events") {
      return {
        labels: [t("common.in"), t("common.out"), t("common.pending")],
        colors: ["#058900", "#2563EB", "#F97C00"],
        title: t("common.total-user"),
        emptyText: t("common.no-data"),
      };
    }

    if (chartType === "requests") {
      return {
        labels: [
          t("common.approved"),
          t("common.rejected"),
          t("common.pending"),
        ],
        colors: ["#058900", "#D00D00", "#F97C00"],
        title: t("common.total-request"),
        emptyText: t("common.no-data"),
      };
    }

    return {
      labels: [t("common.in"), t("common.out"), t("common.unidentified-user")],
      colors: ["#058900", "#2563EB", "#9CA3AF"],
      title: t("common.total-image"),
      emptyText: t("common.no-data"),
    };
  }, [chartType, t]);

  const { segment1, segment2, segment3, total } = chartCounts;
  const activeSegments = [segment1, segment2, segment3].filter(
    (item) => item > 0
  ).length;
  const hasMultipleSegments = activeSegments > 1;

  const chartData = useMemo(
    () => ({
      labels: chartConfig.labels,
      datasets: [
        {
          data: [segment1, segment2, segment3],
          backgroundColor: chartConfig.colors,
          borderWidth: 0,
          spacing: hasMultipleSegments ? 2 : 0,
          borderRadius: hasMultipleSegments ? 8 : 0,
        },
      ],
    }),
    [segment1, segment2, segment3, chartConfig, hasMultipleSegments]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: { display: false },
        [centerPluginId]: {
          title: chartConfig.title,
          total,
        },
      },
    }),
    [centerPluginId, chartConfig.title, total]
  );

  const legendData = chartConfig.labels.map((label, index) => ({
    color: chartConfig.colors[index],
    display: label,
    value: [segment1, segment2, segment3][index],
  }));

  return (
    <div className="px-2 sm:px-4 py-3 sm:py-4 h-full w-full min-w-0 flex flex-col justify-between gap-3">
      <div className="flex items-center justify-center flex-1 min-h-0 w-full">
        <div
          ref={chartContainerRef}
          className="aspect-square w-full max-w-full mx-auto"
        >
          {total > 0 ? (
            <Doughnut
              key={`${chartType}-${chartContainerWidth}`}
              data={chartData}
              options={options}
              plugins={[centerTextPlugin]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-center text-gray-400 text-xs sm:text-sm px-3 sm:px-5">
                {chartConfig.emptyText}
              </p>
            </div>
          )}
        </div>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-8 gap-y-2 sm:gap-y-3 px-1 sm:px-2 w-full">
          {legendData.map((item, index) => (
            <div key={index} className="flex items-center justify-center gap-1.5 py-0.5 sm:py-1 max-w-full">
              <div
                className="rounded-full w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <p className="text-[10px] sm:text-xs text-[#262626] truncate">
                {item.display}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventLogChart;
